import { Config } from 'core/common/contexts';
import { normalizeError } from 'core/common/errors';
import { HttpClient } from 'core/common/services';
import { Logger } from 'core/common/services/logger/interfaces';
import { stringifyQueryParams } from 'core/common/utils/getQueryParams';
import { removeLeadingSlash } from 'core/common/utils/string';
import { uniq } from 'core/common/utils/uniq';
import { uniqBy } from 'core/common/utils/uniqBy';
import {
  FunnelConfig,
  FunnelRequestFilters,
  LightFunnel,
  LightFunnelRequestFilters,
  StaticProps,
} from 'core/funnel/entities';
import { Languages } from 'core/localization/entities';
import { FunnelService } from '../../interfaces';
import { FunnelDtoMapper } from '../../mappers';
import { FunnelDto, LightFunnelDto } from './dto';

export class HttpFunnelService implements FunnelService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly logger: Logger,
    private readonly config: Config,
  ) {}

  async getFunnels(filters?: Partial<FunnelRequestFilters>): Promise<Array<FunnelConfig> | null> {
    try {
      const dtoMapper = new FunnelDtoMapper();

      const queryParams = stringifyQueryParams({
        funnel_name: filters?.funnelName,
        funnel_link: filters?.funnelLink,
        funnel_status: filters?.funnelStatus,
        flow_name: filters?.flowName,
        flow_status: filters?.flowStatus,
        language: filters?.language,
      });

      const { data } = await this.httpClient.get<{ funnels: Array<FunnelDto> }>(
        `/funnel-builder/external/funnels?${queryParams}`,
      );

      return data.funnels.map(dtoMapper.toEntity);
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);

      if (this.config.isBuildPhase) {
        throw error;
      }

      return null;
    }
  }

  async getFunnelsRawResponse(
    filters?: Partial<FunnelRequestFilters>,
  ): Promise<Array<FunnelDto> | null> {
    try {
      const queryParams = stringifyQueryParams({
        funnel_name: filters?.funnelName,
        funnel_link: filters?.funnelLink,
        funnel_status: filters?.funnelStatus,
        flow_name: filters?.flowName,
        flow_status: filters?.flowStatus,
        language: filters?.language,
      });

      const { data } = await this.httpClient.get<{ funnels: Array<FunnelDto> }>(
        `/funnel-builder/external/funnels?${queryParams}`,
      );

      return data.funnels;
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);

      if (this.config.isBuildPhase) {
        throw error;
      }

      return null;
    }
  }

  async getLightFunnels(filters: LightFunnelRequestFilters): Promise<Array<LightFunnel> | null> {
    try {
      const dtoMapper = new FunnelDtoMapper();

      const queryParams = stringifyQueryParams({ funnel_link: filters.funnelLink });

      const { data } = await this.httpClient.get<{ funnels: Array<LightFunnelDto> }>(
        `/funnel-builder/external/funnels/light?${queryParams}`,
      );

      const lightFunnels = data.funnels.map((funnel) => dtoMapper.toLightEntity(funnel));

      return lightFunnels;
    } catch (err) {
      const error = normalizeError(err);
      this.logger.error(error);

      if (this.config.isBuildPhase) {
        throw error;
      }

      return null;
    }
  }

  getFunnelsPaths(funnels: Array<FunnelConfig>) {
    const paths: StaticProps[] = [];

    funnels.forEach((funnel) => {
      funnel.flows.forEach((flow) => {
        if (!this.isSupportedLocale(flow.language)) {
          return;
        }

        paths.push({
          params: { funnel: removeLeadingSlash(funnel.link) },
          locale: flow.language,
        });
      });
    });

    return paths;
  }

  getFunnelsPathsWithSubRouts(funnels: Array<FunnelConfig>) {
    const paths: StaticProps[] = [];

    funnels.forEach((funnel) => {
      const { flows } = funnel;

      const funnelScreens = uniqBy(
        flows.flatMap(({ screens }) => screens),
        'id',
      );

      const availableLocales = uniq(flows.map(({ language }) => language)).filter((locale) =>
        this.isSupportedLocale(locale),
      );

      const pathsCombined = availableLocales.flatMap((locale) =>
        funnelScreens.map((step) => ({
          params: {
            id: step.id,
            funnel: removeLeadingSlash(funnel.link),
          },
          locale,
        })),
      );

      paths.push(...pathsCombined);
    });

    return paths;
  }

  private isSupportedLocale(locale: string): boolean {
    const isSupported = (Object.values(Languages) as string[]).includes(locale);

    if (!isSupported) {
      this.logger.warn(`Skipping unsupported locale "${locale}"`);
    }

    return isSupported;
  }
}
